/**
 * Shared validation for the lead capture form. Used by both the client
 * component and the API route, so the two cannot drift.
 */

// Validation constants
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PHONE_ALLOWED_CHARS = /^[0-9+\-() ]+$/;
export const MIN_PHONE_DIGITS = 7;
export const MAX_PHONE_DIGITS = 15;
export const MAX_EMAIL_LENGTH = 254;
export const MAX_COMPANY_LENGTH = 100;
export const MAX_PHONE_LENGTH = 20;

/**
 * Strip angle brackets. Not an XSS defence — these values are written to
 * spreadsheet cells and an email body, never to HTML.
 */
export function sanitizeText(text: string): string {
	return text.replace(/[<>]/g, "").trim();
}

/** Leading characters a spreadsheet reads as the start of a formula. */
export const FORMULA_LEAD = /^[=+\-@\t\r|]/;

/**
 * Sheets runs a leading `=`, `+`, `-`, `@` as a formula, and ignores whitespace
 * before it, so the run has to be stripped as a whole: "= =SUM(A1)" is hostile.
 */
export function stripFormulaPrefix(text: string): string {
	return text.replace(/^[\s=+\-@|]+/, "");
}

/**
 * Extract only digits from phone number for length validation.
 */
export function getPhoneDigits(phone: string): string {
	return phone.replace(/\D/g, "");
}

/**
 * Validate email and return error message or null if valid.
 */
export function validateEmail(value: string): string | null {
	const trimmed = value.trim();
	if (!trimmed) return "Email is required";
	if (trimmed.length > MAX_EMAIL_LENGTH) return "Email is too long";
	if (!EMAIL_REGEX.test(trimmed)) return "Enter a valid email address";
	if (FORMULA_LEAD.test(trimmed)) return "Enter a valid email address";
	return null;
}

/**
 * The company name as it will be stored: markup and the characters a
 * spreadsheet reads as a formula both removed.
 */
export function normalizeCompany(value: string): string {
	return stripFormulaPrefix(sanitizeText(value));
}

/**
 * Validate company name and return error message or null if valid. Validates
 * the normalized form, so a value of "=" is rejected rather than accepted here
 * and then stored empty.
 */
export function validateCompany(value: string): string | null {
	const normalized = normalizeCompany(value);
	if (!normalized) return "Company name is required";
	if (normalized.length > MAX_COMPANY_LENGTH) return "Company name is too long";
	return null;
}

/**
 * Validate phone number and return error message or null if valid.
 */
export function validatePhone(value: string): string | null {
	const trimmed = value.trim();
	if (!trimmed) return "Phone number is required";
	if (trimmed.length > MAX_PHONE_LENGTH) return "Phone number is too long";
	if (!PHONE_ALLOWED_CHARS.test(trimmed)) return "Phone contains invalid characters";
	const digits = getPhoneDigits(trimmed);
	if (digits.length < MIN_PHONE_DIGITS) return "Phone number is too short";
	if (digits.length > MAX_PHONE_DIGITS) return "Phone number is too long";
	return null;
}

/** UTM keys forwarded with a lead so the sheet can attribute it. */
export const UTM_KEYS = [
	"utm_source",
	"utm_medium",
	"utm_campaign",
	"utm_content",
	"utm_id",
] as const;

/**
 * Read UTM parameters off the current URL. Deliberately independent of the
 * analytics/consent path: attribution on a lead the visitor chose to send is
 * not tracking, and gating it there left the sheet's columns always empty.
 */
export function readUtmParams(search: string): Record<string, string> {
	const params = new URLSearchParams(search);
	const utm: Record<string, string> = {};
	for (const key of UTM_KEYS) {
		const value = params.get(key);
		if (value) utm[key] = value;
	}
	return utm;
}
